import React, { createContext, useContext, useMemo, useState } from 'react';

import { Event, EventAttendee } from 'app/shared/typings';
import { AuthContext } from 'app/shared/context/Auth';
import {
  PromoCodeValue,
  StripeVirtualWallet,
  usePromoCode,
  useVirtualWallet,
} from 'app/fan/utils/checkout';

interface ExpressCheckoutContextProviderProps {
  event: Event;
  eventAttendee?: EventAttendee;
}

export interface ExpressCheckoutContextValue {
  event: Event;
  eventAttendee?: EventAttendee;
  numberOfTickets: number;
  setNumberOfTickets: any;
  optinGuestMarketing: boolean;
  setOptinGuestMarketing: any;
  stripeVirtualWallet?: StripeVirtualWallet;
  promoCodeValue: PromoCodeValue;
}

const ExpressCheckoutContext =
  createContext<ExpressCheckoutContextValue | null>(null);

export const useExpressCheckoutContext: () => ExpressCheckoutContextValue =
  () => {
    const value = useContext(ExpressCheckoutContext);

    // Check if there is a parent ExpressCheckoutContextProvider
    if (value == null) {
      throw new Error(
        'useExpressCheckoutContext must be called within a child or grandchild of an Express Checkout Context Provider!'
      );
    }
    return value;
  };

export const ExpressCheckoutContextProvider: React.FC<
  React.PropsWithChildren<ExpressCheckoutContextProviderProps>
> = ({ event, eventAttendee, children }) => {
  const { user, loggedIn } = useContext(AuthContext);
  const [numberOfTickets, setNumberOfTickets] = useState(2);
  const [optinGuestMarketing, setOptinGuestMarketing] = useState(true);
  const promoCodeValue = usePromoCode(event.id);
  const stripeVirtualWallet = useVirtualWallet({
    isGuestCheckout: !loggedIn,
    isExpressCheckout: true,
    user,
    event,
    eventAttendee,
    numberOfTickets,
    optinGuestMarketing,
    promoCodeWithCharges: promoCodeValue.promoCodeWithCharges,
  });

  const expressCheckoutContextValue = useMemo(
    () => ({
      event,
      eventAttendee,
      numberOfTickets,
      setNumberOfTickets,
      optinGuestMarketing,
      setOptinGuestMarketing,
      stripeVirtualWallet,
      promoCodeValue,
    }),
    [
      event,
      eventAttendee,
      stripeVirtualWallet,
      numberOfTickets,
      optinGuestMarketing,
      promoCodeValue,
    ]
  );

  return (
    <ExpressCheckoutContext.Provider value={expressCheckoutContextValue}>
      {children}
    </ExpressCheckoutContext.Provider>
  );
};
